import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Link as MuiLink,
  FormHelperText,
  Typography,
  Stack,
} from "@mui/material";
import type { ApiCallStatus } from "@/features/dmManagement/types";
import { validateAddressListFile } from "@/features/dmManagement/utils/fileValidation";

interface AddressListUploadProps {
  onFileSelect: (file: File) => void;
  onFileRejected?: () => void;
  parseStatus: ApiCallStatus;
  parseError: string | null;
  showTitle?: boolean;
}

/**
 * 宛先リストアップロード
 * CSV/Excelファイルの選択・バリデーションを行い、テンプレートのダウンロードリンクを提供する
 */
export const AddressListUpload: React.FC<AddressListUploadProps> = ({
  onFileSelect,
  onFileRejected,
  parseStatus,
  parseError,
  showTitle = true,
}) => {
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // ref 経由で最新の onFileRejected を参照することで、親で memo されていない場合のリスナ再登録を防ぐ
  const onFileRejectedRef = useRef(onFileRejected);
  useEffect(() => {
    onFileRejectedRef.current = onFileRejected;
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) {
      setValidationError(null);
      onFileRejected?.();
      return;
    }

    const error = validateAddressListFile(file);
    if (error) {
      setValidationError(error);
      onFileRejected?.();
      return;
    }

    setValidationError(null);
    onFileSelect(file);
  };

  // ファイル選択ダイアログのキャンセルは、ブラウザによって onChange の空発火か HTMLInputElement の cancel イベントのいずれかで通知される。
  // React 18 の型に onCancel が無いため addEventListener で購読する
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const handleCancel = (): void => {
      setValidationError(null);
      // input の value を空にしないと、選択中のファイル名表示が残り、また同一ファイル再選択時に onChange が発火しない
      input.value = "";
      onFileRejectedRef.current?.();
    };
    input.addEventListener("cancel", handleCancel);
    return () => {
      input.removeEventListener("cancel", handleCancel);
    };
  }, []);

  const displayError = validationError ?? parseError;

  return (
    <Box>
      {showTitle && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          宛先リスト
        </Typography>
      )}
      <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
        <MuiLink
          href="/assets/template_DM_宛先リスト.csv"
          download
          variant="body2"
        >
          テンプレートCSVをダウンロード
        </MuiLink>
        <MuiLink
          href="/assets/template_DM_宛先リスト.xlsx"
          download
          variant="body2"
        >
          テンプレートExcelをダウンロード
        </MuiLink>
      </Stack>
      <Box>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx"
          onChange={handleFileChange}
          aria-label="宛先リストファイル"
        />
        {parseStatus === "loading" && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            パース中...
          </Typography>
        )}
        {displayError && <FormHelperText error>{displayError}</FormHelperText>}
      </Box>
    </Box>
  );
};
